import * as pc from 'playcanvas';

import { applyTransform } from './apply-transform';
import type { MeshElement } from './scene-types';

/**
 * Load a single .glb (or .gltf) and instantiate it as a render-component
 * entity. We use PlayCanvas's container asset, which is the engine's
 * canonical way to load glTF (the resulting `resource` is a `ContainerResource`
 * exposing `model`, `materials`, `animations`, etc).
 *
 * For viewer2 we only need the rendered model; animations and materials are
 * accessed via the container if effect keyframes need to mutate them later.
 */
export async function loadModel(
    app: pc.AppBase,
    parent: pc.Entity,
    el: MeshElement,
    absoluteUrl: string
): Promise<pc.Entity> {
    const asset = new pc.Asset(`model:${el.id}`, 'container', { url: absoluteUrl });
    app.assets.add(asset);

    await new Promise<void>((resolve, reject) => {
        asset.once('load', () => resolve());
        asset.once('error', (err: unknown) => {
            reject(new Error(`model "${el.id}" failed to load: ${String(err)}`));
        });
        app.assets.load(asset);
    });

    const container = asset.resource as pc.ContainerResource;
    const entity = container.instantiateRenderEntity();
    entity.name = `model:${el.id}`;
    applyTransform(entity, el.transform);
    parent.addChild(entity);

    return entity;
}
